/**
 * NOTE: This file can have treatments added to it, but treatments SHOULD NEVER be removed.
 * This file is used to maintain treatments since the first treatment was applied.
 */

/** Map of treatment primitive types to their named constants */
export const TreatmentPrimitiveTypes = {
  CONTRAST: 'CONTRAST',
  DICHOPTIC: 'DICHOPTIC',
  FLICKER: 'FLICKER',
}

export type TreatmentPrimitiveType = keyof typeof TreatmentPrimitiveTypes

/** Interface representing an individual treatment instruction */
export interface ITreatmentPrimitive<Type extends TreatmentPrimitiveType, Params> {
  type: Type
  params: Params
}

/**
 * Should the primitive target the weak or strong eye?
 */
export const TargetEyes = {
  ['$WEAK']: '$WEAK',
  ['$STRONG']: '$STRONG',
}

export type TargetEye = keyof typeof TargetEyes

/**
 * A template string specifying a variable contrast weight.
 * The template string must be converted to a valid numeric contrast weight prior to exposure to API consumers.
 */
export const ContrastWeightTemplates = {
  /** Incrementing contrast 1 for the fellow eye */
  $INC_1_F: '$INC_1_F',
  /** Incrementing contrast 1 for the amblyopic eye */
  $INC_1_A: '$INC_1_A',
}

/**
 * A treatment that reduces the contrast of an image in one eye. The contrast reduction is
 * performed on the luminance of each pixel, with the calculation:
 *
 * (L - 0.5) * weight + 0.5
 *
 * - _L_ being the luminance of a given pixel
 * - _weight_ being a value between 0 and 200
 *
 * Contrast weight may also be a template string `ContrastWeightTemplates` for variable contrast treatments.
 * The template string must be replaced with a valid numeric weight before exposing to API consumers.
 *
 */
export type CONTRAST = ITreatmentPrimitive<'CONTRAST', {
  eye: TargetEye,
  /**
   * Integer range [0, 100] or valid contrast weight template string
   */
  weight: number | keyof typeof ContrastWeightTemplates,
}>

/**
 * A treatment that causes loosely inverse masks to be presented over both of the video
 * presentations. The mask with the most obstruction in the area to have the most foveal
 * attention should be placed on the eye specified in this treatment instruction's parameters.
 *
 * `primaryEye` describes which eye should receive the most obstruction.
 *
 * `rotationFrequency` describes how often the masks should be rotated out for a new pair of masks.
 */
export type DICHOPTIC = ITreatmentPrimitive<'DICHOPTIC', {
  // TODO rename to `clearEye`
  /**
   * Which eye should receive the mask with the unoccluded center?
   */
  primaryEye: TargetEye,
  /**
   * Positive integer milliseconds
   */
  rotationFrequency: number,
}>

export const FlickerStartPhases = {
  Normal: 'Normal',
  Proxy: 'Proxy',
}

export type FlickerStartPhase = keyof typeof FlickerStartPhases

/**
 * A treatment that causes the video to be displayed for a period of time (_normalDuration_),
 * then a separate uniform color (_proxyColor_) to be displayed for another period of time
 * (_proxyDuration_). The flicker will either start in the Normal or Proxy phase (_startPhase_)
 * with a period offset defined by (_offset_). All time values are in milliseconds.
 */
export type FLICKER = ITreatmentPrimitive<'FLICKER', {
  eye: TargetEye,
  startPhase: FlickerStartPhase,
  /**
   * Integer milliseconds
   */
  offset: number,
  /**
   * Positive integer milliseconds
   */
  normalDuration: number,
  /**
   * Positive integer milliseconds
   */
  proxyDuration: number,
  /**
   * Hex color code. E.g. "#4D4D4D"
   */
  proxyColor: string,
}>

export type TreatmentPrimitive = CONTRAST | DICHOPTIC | FLICKER



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./core/src/api/prescription/treatment/primitives.ts